import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, startGoogleLogin } from "@/auth";
import { Seo } from "@/components/Seo";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.1z" />
    <path fill="#EA4335" d="M12 4.77c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.88 8.87 4.77 12 4.77z" />
  </svg>
);

export default function SignIn() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [agreeLegal, setAgreeLegal] = useState(false);
  const [inAustralia, setInAustralia] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/account", { replace: true });
  }, [user, loading, navigate]);

  const ready = agreeLegal && inAustralia;

  return (
    <main data-testid="signin-page" className="flex min-h-[80vh] items-center pt-32 pb-24">
      <Seo
        title="Sign In — DISJINX"
        description="Sign in to your DISJINX account to manage your AI automations and billing."
        path="/signin"
        noindex
      />
      <div className="mx-auto w-full max-w-xl px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">Sign in</p>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight lg:text-5xl">Before you continue.</h1>
          <p className="mt-4 text-base leading-relaxed text-neutral-600">
            DISJINX is built for Australian businesses. Please confirm both statements, then continue with Google.
          </p>

          <div className="mt-10 space-y-4">
            <label
              data-testid="signin-consent-legal-label"
              className="flex cursor-pointer items-start gap-3 border border-neutral-200 bg-white p-5 transition-colors duration-200 hover:border-neutral-950"
            >
              <input
                type="checkbox"
                data-testid="signin-consent-legal-checkbox"
                checked={agreeLegal}
                onChange={(e) => setAgreeLegal(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-neutral-950"
              />
              <span className="text-sm leading-relaxed text-neutral-700">
                I have read and agree to the{" "}
                <Link to="/privacy" data-testid="signin-privacy-link" className="font-semibold text-neutral-950 underline underline-offset-2">
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link to="/terms" data-testid="signin-terms-link" className="font-semibold text-neutral-950 underline underline-offset-2">
                  Terms and Conditions
                </Link>
                .
              </span>
            </label>

            <label
              data-testid="signin-consent-australia-label"
              className="flex cursor-pointer items-start gap-3 border border-neutral-200 bg-white p-5 transition-colors duration-200 hover:border-neutral-950"
            >
              <input
                type="checkbox"
                data-testid="signin-consent-australia-checkbox"
                checked={inAustralia}
                onChange={(e) => setInAustralia(e.target.checked)}
                className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-neutral-950"
              />
              <span className="text-sm leading-relaxed text-neutral-700">I confirm that I am located in Australia.</span>
            </label>
          </div>

          <button
            data-testid="signin-google-button"
            onClick={startGoogleLogin}
            disabled={!ready}
            className="mt-10 flex w-full items-center justify-center gap-3 bg-neutral-950 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <GoogleIcon />
            Continue with Google
          </button>
          {!ready && (
            <p data-testid="signin-consent-hint" className="mt-3 text-xs text-neutral-500">
              Tick both boxes to continue.
            </p>
          )}
        </motion.div>
      </div>
    </main>
  );
}
